"use client";

import React from 'react';
import styles from './_gc-step-shell.module.scss';
import Button from '@/components/button';

/**
 * @description GC OPTIMIZE™ Calculator — Step shell
 * White card wrapper for every wizard step. Renders a title header with
 * eggplant rule, step body (children render as direct flex items), a
 * right-aligned action row (Back + Save/Next), and an optional disclaimer.
 *
 * @param {string}    title          — step heading text
 * @param {ReactNode} children       — step body sections (each should be a div)
 * @param {Function}  onBack         — navigate to previous step
 * @param {Function}  onNext         — navigate to next step / submit
 * @param {boolean}   showBack       — hide Back button on first step (default true)
 * @param {boolean}   isNextDisabled — disable Next when required fields are empty
 * @param {string}    nextLabel      — override Next button label (default "Save & continue")
 * @param {ReactNode} disclaimer     — optional disclaimer node rendered below button
 * @param {number}    stepNumber     — numeric step index for GTM tracking
 */
function GcStepShell({
  title,
  children,
  onBack,
  onNext,
  showBack = true,
  isNextDisabled = false,
  isReview = false,
  nextLabel = 'Save & continue',
  nextVariant = ['solid', 'solid--eggplant'],
  nextIconType,
  disclaimer,
  stepNumber,
  innerStep = 0,
}) {
  // In review mode, show icon-only orange restart button
  const resolvedVariant = isReview ? ['solid', 'solid--orange'] : nextVariant;
  const resolvedIconType = isReview ? 'restart' : nextIconType;
  const resolvedLabel = isReview ? '' : nextLabel;

  return (
    <div className={styles.gc_step_shell}>
      <header className={styles.header}>
        <h2 className={styles.title}>{title}</h2>
      </header>

      {children}

      <div className={styles.actions}>
        {showBack && (
          <Button
            type="button"
            variant="outline--eggplant"
            customClass={styles.btn_back}
            onClick={onBack}
            data-gtm-event-category="gc-calculator"
            data-gtm-event-action="Click"
            data-gtm-event-label="Back"
            data-step={title}
            data-step-number={`${stepNumber}.${innerStep}`}
          >
            Back
          </Button>
        )}
        <Button
          type="button"
          variant={resolvedVariant}
          iconType={resolvedIconType}
          customClass={`${styles.btn_next}${isReview ? ` ${styles.btn_restart}` : ''}`}
          onClick={onNext}
          disabled={isNextDisabled}
          aria-label={isReview ? 'Restart from this step' : undefined}
          data-gtm-event-category="gc-calculator"
          data-gtm-event-action="Click"
          data-gtm-event-label={isReview ? 'Restart' : nextLabel}
          data-save={nextLabel === 'Save & continue'}
          data-step={title}
          data-step-number={`${stepNumber}.${innerStep}`}
        >
          {resolvedLabel}
        </Button>
      </div>

      {disclaimer && (
        <div className={styles.disclaimer}>{disclaimer}</div>
      )}
    </div>
  );
}

export default GcStepShell;
